import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  CapaActivityType,
  CapaQuotationUserBizType,
  UpdateQuotationUserInput
} from '@generated/graphql';
import { Divider, Grid } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { IClientState } from '@modules/client/types';
import useMyPage from '@hooks/useMyPage';
import { useStyles } from './styles';
import { MyAccountContext } from '../../MyAccountContainer';
import { clsx } from '@components/Provider';
import DisplayItem from './DisplayItems';
import ClientTypeEditDialog from '../Dialog/ClientTypeEditDialog';
import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';
import CompanyInfoEditDialog from '../Dialog/CompanyInfoEditDialog';
import Dialog from '@components/Dialog';
import useClient from '@hooks/useClient';
import PhoneNumberItem from './PhoneNumberItem';

interface Props {
  onChangePath: (path: string) => void;
  onUpdate: (updateData: Partial<IClientState>) => void;
}

const ProfilePresenter: React.FC<Props> = ({ onChangePath, onUpdate }) => {
  const history = useHistory();
  const context = useContext(MyAccountContext);
  const { clientState } = useClient();
  const classes = useStyles();
  const [openDialog, setOpenDialog] = useState({
    postcode: false,
    companyRegistrationNumber: false,
    company_info: false
  });
  const { onInsertUserActionLogOne } = useUserActionLog();
  const { onUpdateProfileField } = useMyPage();

  const clientType = () => {
    switch (clientState?.bizType) {
      case CapaQuotationUserBizType.Organization:
        return '비사업자';
      case CapaQuotationUserBizType.Individual:
        return '비사업자';
      case CapaQuotationUserBizType.Biz:
        return '사업자';
      default:
        return '고객 유형이 등록되지 않았습니다.';
    }
  };

  const departmentAndPositionText = () => {
    const department =
      !clientState?.department || clientState.department === null
        ? undefined
        : clientState.department;
    const position =
      !clientState?.position || clientState.position === null
        ? undefined
        : clientState.position;

    if (department && position) {
      return `${department}, ${position}`;
    }

    if (!department && position) {
      return position;
    }

    if (department && !position) return department;

    return '미기입';
  };

  return (
    <Grid container direction="column" classes={{ root: classes.root }}>
      <CustomizedTypography className={classes.myTitle}>
        내 정보
      </CustomizedTypography>

      <DisplayItem
        label="이메일"
        value={
          clientState?.email || context.DecodedaccessTokenEmail || '미기입'
        }
        onClick={({ content, label }) => {
          onUpdateProfileField({
            field: [
              {
                fieldName: 'email',
                content
              }
            ],
            label,
            placeholder: ['이메일을 입력하세요.']
          });
          history.push('/mypage/edit');
        }}
      />

      <Divider className={classes.divider} />

      <DisplayItem
        label="비밀번호"
        value={'***********'}
        onClick={() => {
          history.push('/login/resetpassword');
        }}
      />

      <Divider className={classes.divider} />

      <DisplayItem
        label="이름 (담당자명)"
        value={clientState?.managerName || '미기입'}
        onClick={({ content, label }) => {
          onUpdateProfileField({
            field: [
              {
                fieldName: 'managerName',
                content
              }
            ],
            label,
            placeholder: ['특수문자 없이 20자 이내로 입력해주세요.']
          });
          history.push('/mypage/edit');
        }}
      />

      <Divider className={classes.divider} />

      <PhoneNumberItem />

      <Divider className={classes.divider} />

      <DisplayItem
        label="사업자여부"
        value={clientType() || '미기입'}
        onClick={() => {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Mypage_Update_IsBiz,
            CapaActivityType.ButtonClick
          );
          setOpenDialog({ ...openDialog, companyRegistrationNumber: true });
        }}
      />

      {clientState?.bizType === CapaQuotationUserBizType.Biz && (
        <>
          <Divider className={classes.divider} />

          <DisplayItem
            label="부서 및 직함"
            value={departmentAndPositionText()}
            onClick={({ content, label }) => {
              onUpdateProfileField({
                field: [
                  {
                    fieldName: 'department',
                    content: content.split(', ')[0] ?? ''
                  },
                  {
                    fieldName: 'position',
                    content: content.split(', ')[1] ?? ''
                  }
                ],
                label,
                placeholder: ['부서를 입력해주세요.', '직함을 입력해주세요.']
              });
              history.push('/mypage/edit');
            }}
          />
        </>
      )}

      <Divider className={classes.divider} />

      {clientState?.bizType === CapaQuotationUserBizType.Biz && (
        <>
          <Grid container className={classes.companyTitleContainer}>
            <CustomizedTypography className={classes.companyTitle}>
              회사정보
            </CustomizedTypography>
          </Grid>

          <DisplayItem
            label="사업자등록번호"
            value={clientState?.companyRegistrationNumber || '미기입'}
            onClick={() => {
              setOpenDialog({ ...openDialog, company_info: true });
            }}
          />

          <Divider className={classes.divider} />

          <DisplayItem
            label={'회사명'}
            value={clientState?.organizationName || ''}
            onClick={() => {
              setOpenDialog({ ...openDialog, company_info: true });
            }}
          />

          <Divider className={classes.divider} />

          <DisplayItem
            label="주소"
            value={clientState?.fullAddress || '미기입'}
            onClick={() => {
              setOpenDialog({ ...openDialog, postcode: true });
            }}
          />

          <Divider className={classes.divider} />
        </>
      )}
      <Grid className={clsx(classes.item, 'bottom')} item>
        <CustomizedButton
          variant="text"
          color="secondary"
          onClick={() => onChangePath('/mypage/membership-withdrawal')}
        >
          회원탈퇴
        </CustomizedButton>
      </Grid>

      <ClientTypeEditDialog
        open={openDialog.companyRegistrationNumber}
        onClose={() =>
          setOpenDialog({ ...openDialog, companyRegistrationNumber: false })
        }
        onUpdate={(data: UpdateQuotationUserInput) => onUpdate(data)}
      />
      <CompanyInfoEditDialog
        open={openDialog.company_info}
        onClose={() => setOpenDialog({ ...openDialog, company_info: false })}
        onUpdate={(data: UpdateQuotationUserInput) => onUpdate(data)}
      />

      <Dialog.Address.PostcodeDialog
        open={openDialog.postcode}
        onClose={() => setOpenDialog({ ...openDialog, postcode: false })}
        onSubmit={onUpdate}
      />
    </Grid>
  );
};

export default ProfilePresenter;
